import type { ViewerSkinLevel, ViewerSkinRole } from 'core/utils/viewerSkin';

type BuildPdfJsToolbarParams = {
  isDefaultToolbar: boolean;
  level: ViewerSkinLevel;
  role: ViewerSkinRole;
  toolbar: string[] | null | undefined;
};

export const DEFAULT_VIEWER_TOOLBAR = [
  'TOC_AND_THUMBNAILS',
  'COMMENTS',
  'HOTSPOTS',
  'HAND',
  'SELECT_TEXT',
  'DRAWING',
  'TEXT',
  'SHAPES',
  'ERASER',
  'SEARCH',
  'PRINT',
  'VIEW_CONTROLS',
  'ZOOM_IN',
  'ZOOM_OUT',
];

export const INFANTIL_EXCLUDED_TOOLBAR_ITEMS = ['TOC_AND_THUMBNAILS', 'HOTSPOTS', 'VIEW_CONTROLS', 'TEXT'];

// Token de exclusión: oculta el «Panel lateral» que abre la pestaña de anotaciones, porque COMMENTS se mantiene para guardarlas.
const INFANTIL_EXTRA_TOOLBAR_ITEMS = ['HIDE_SIDEBAR_PANEL'];

function hasItem(items: string[], item: string): boolean {
  return items.some((current) => String(current).toUpperCase() === item);
}

// Sin SEARCH por decisión de producto, no por el motor: PDF.js tiene búsqueda desde 0.1.64.
function toPdfJsDefaultToolbar(toolbar: string[]): string[] {
  const items = toolbar.filter((item) => String(item).toUpperCase() !== 'SEARCH');

  return hasItem(items, 'DOWNLOAD') ? items : [...items, 'DOWNLOAD'];
}

// El profesor conserva la toolbar completa en cualquier nivel; solo el alumno de infantil la ve reducida.
function toInfantilStudentToolbar(toolbar: string[]): string[] {
  const items = toolbar.filter((item) => !INFANTIL_EXCLUDED_TOOLBAR_ITEMS.includes(String(item).toUpperCase()));
  const extraItems = INFANTIL_EXTRA_TOOLBAR_ITEMS.filter((item) => !hasItem(items, item));

  return [...items, ...extraItems];
}

/** Toolbar del motor PDF.js: retoca solo la toolbar por defecto; la regla de infantil + alumno se aplica siempre porque solo oculta. */
export function buildPdfJsToolbar({ toolbar, isDefaultToolbar, level, role }: BuildPdfJsToolbarParams): string[] | null | undefined {
  if (!Array.isArray(toolbar)) return toolbar;

  const items = isDefaultToolbar ? toPdfJsDefaultToolbar(toolbar) : toolbar;

  return level === 'infantil' && role === 'student' ? toInfantilStudentToolbar(items) : items;
}
